import React, { useEffect, useState } from 'react';
import './css/Register.css';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import Switch from '@mui/material/Switch';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/AddCircle';
import {CircularProgress, IconButton} from "@mui/material";
import TextField from "@mui/material/TextField";
import {AutoGraphTwoTone, EditTwoTone} from "@mui/icons-material";
import {useLocation} from "react-router-dom";
import Cookies from "js-cookie";

function PerformanceMachines({ onAction }) {
    return <MachinesSection onMachinesUpdated={onAction} />;
}

function MachinesSection({ onMachinesUpdated }) {
    const [machines, setMachines] = useState([]);
    const [newMachineId, setNewMachineId] = useState('');
    const [authToken, setAuthToken] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [isAdding, setIsAdding] = useState(false);

    useEffect(() => {
        const token = Cookies.get('auth');
        if (token) {
            setLoading(true);
            setAuthToken(token);
            fetch(`../api/perf/getMachines?token=${encodeURIComponent(token)}`)
                .then(response => response.json())
                .then(data => {
                    setMachines(data);
                    setLoading(false);
                })
                .catch(error => {
                    console.error('Error fetching machines:', error);
                    setLoading(false);
                });
        } else {
            console.log('No token present in cookies.');
        }
    }, []);

    const handleAddMachine = () => {
        setIsAdding(true);
        const isExisting = machines.some(machine => machine.machineId.toString() === newMachineId);
        if (isExisting) {
            setErrorMessage('Machine already added.');
            setIsAdding(false);
            return;
        }

        // If newMachineId is unique, attempt to add the machine
        fetch(`../api/perf/createMachine?token=${encodeURIComponent(authToken)}&m_id=${newMachineId}`, {
            method: 'GET', // Assuming GET, update if necessary
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Machine does not exist');
                }
                return response.json();
            })
            .then(data => {
                setMachines(currentMachines => [...currentMachines, data]);
                setNewMachineId('');
                setErrorMessage('');
                onMachinesUpdated();
                setIsAdding(false);
            })
            .catch(error => {
                setErrorMessage(error.message);
                setIsAdding(false);
            });
    };

    const handleDeleteMachine = (machineId) => {
        const previousMachines = [...machines]; // Keep a copy of the current state to roll back in case of an error
        setMachines(currentMachines => currentMachines.filter(machine => machine.machineId !== machineId));
        onMachinesUpdated();

        // Construct the URL with machineId
        const url = `../api/perf/delMachine?token=${encodeURIComponent(authToken)}&m_id=${machineId}`;

        // Perform the fetch request
        fetch(url, {
            method: 'GET', // Assuming GET, update if necessary
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to delete machine');
                }
                // Optionally, filter out the deleted machine from the machines state
                setMachines(currentMachines => currentMachines.filter(machine => machine.machineId !== machineId));
                onMachinesUpdated();
            })
            .catch(error => {
                console.error('Error deleting machine:', error);
                alert('Error deleting machine');
                setMachines(previousMachines);
                onMachinesUpdated();
            });
    };

    return (
        <div className="SectionEmbedded">
            <div className="SectionLtr">
              <span className="SectionBoxEmbedded">
                  <span className="SectionSubHeadingEmbedded">FAVORITE MACHINES</span>
                  {loading ? (
                      // Show spinner in the center when loading
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
                          <CircularProgress />
                      </div>
                  ) : (
                      // Render machines list if not loading and machines are available
                      machines && machines.length > 0 && (
                          <div>
                              <div className="divLineDualGrad"></div>
                              <div className="table-container">
                                  <Table>
                                      <TableHeader />
                                      <TableData machines={machines} onDelete={handleDeleteMachine} setMachines={setMachines} authToken={authToken} onMachinesUpdated={onMachinesUpdated} />
                                  </Table>
                              </div>
                              <div className="divLineDualGrad"></div>
                          </div>
                      )
                  )}
                    <div className="ButtonBarEmbedded">
                      <span className="_button_holder">
                          <TextField error={Boolean(errorMessage)}
                                     helperText={errorMessage}
                                     id="new_machine_id"
                                     focused
                                     label="New Machine ID"
                                     size="small"
                                     value={newMachineId}
                                     onChange={(e) => {
                                         setNewMachineId(e.target.value);
                                         setErrorMessage(''); // Clear error message when user modifies input
                                     }}
                                     style={{ width: 200 }} // Adjust width as needed
                                     InputProps={{
                                          style: {
                                              fontSize: '1.2em', // Smaller font size for the input text
                                          },
                                      }}
                                     InputLabelProps={{
                                         style: {
                                             fontSize: '1.2em', // Smaller font size for the label
                                         },
                                     }}

                                     />
                      </span>
                        <span className="_button_holder">
                            {isAdding ? (
                                <CircularProgress size={32} /> // Adjust size to match the Add button's size
                            ) : (
                                <Button variant="contained" size="medium" startIcon={<AddIcon />} onClick={handleAddMachine}>
                                    Add
                                </Button>
                            )}
                        </span>
                    </div>

              </span>
            </div>
        </div>
    );
}

function TableHeader() {
    return (
        <thead>
        <tr className="trHeader">
            <td>Enabled</td>
            <td>Machine ID</td>
            <td>GPU Type</td>
            <td>Num GPUs</td>
            <td>Verification</td>
            <td colSpan="2">Actions</td>
        </tr>
        </thead>
    );
}

function TableData({ machines, onDelete, setMachines, authToken, onMachinesUpdated }) {
    const handleSwitchChange = (machineId, isEnabled) => {

        // Optimistically update the UI
        const updatedMachines = machines.map(machine => {
            if (machine.machineId === machineId) {
                return { ...machine, enabled: isEnabled };
            }
            return machine;
        });

        setMachines(updatedMachines);
        onMachinesUpdated();

        const url = `../api/perf/enableMachine?token=${encodeURIComponent(authToken)}&m_id=${machineId}&is_e=${isEnabled}`;
        fetch(url, {
            method: 'GET', // Assuming GET, update if necessary
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to update machine status');
                }
                setMachines(currentMachines => currentMachines.map(machine => {
                    if (machine.machineId === machineId) {
                        return { ...machine, enabled: isEnabled };
                    }
                    return machine;
                }));
                onMachinesUpdated();
                console.log('Machine status updated successfully');
            })
            .catch(error => {
                console.error('Error updating machine status:', error);
                const revertedMachines = machines.map(machine => {
                    if (machine.machineId === machineId) {
                        return { ...machine, enabled: !isEnabled };
                    }
                    return machine;
                });

                setMachines(revertedMachines);
                onMachinesUpdated();
            });
    };

    return(
        <tbody>
        {machines.map(machine => (
            <tr key={machine.machineId}>
                <td>
                    <Switch
                        size="small"
                        checked={machine.enabled}
                        onChange={(e) => handleSwitchChange(machine.machineId, e.target.checked)}
                    />
                </td>
                <td>
                    <input value={machine.machineId} className="tableData" readOnly />
                </td>
                <td>
                    <input value={machine.gpuName} className="tableData" readOnly />
                </td>
                <td>
                    <input value={machine.numGpus} className="tableData" readOnly />
                </td>
                <td>
                    <input value={machine.verification} className="tableData" readOnly />
                </td>
                <td>
                    <IconButton color="primary" size="small" onClick={() => onDelete(machine.machineId)}>
                        <DeleteIcon/>
                    </IconButton>
                </td>
            </tr>
        ))}
        </tbody>
    );
}

function AddButton({ onClick }) {
    return (
        <Button variant="contained" size="medium" startIcon={<AddIcon />} onClick={onClick}>
            Add
        </Button>
    );
}

export default PerformanceMachines;
